import { useFetchFeatureFlag } from '../../hooks/queries/useFetchFeatureFlag';
import { useAuth } from '../../hooks/useAuth';
import { useOrganizationId } from '../../hooks/utils/useOrganizationId';
import { PermissionUrn } from '../../pages/UserSettings/Roles/api/permissions.type';
import { useFetchUserPermissions } from './useFetchUserPermissions';
export interface UserPermissionsResponse {
  permissions: PermissionUrn[];
}

export const useFetchUserHasPermissions = ({
  requiredPermissions,
  availableOnlyForPlatformAdmins = false,
}: {
  requiredPermissions: PermissionUrn[];
  availableOnlyForPlatformAdmins?: boolean;
}) => {
  const { checkUserAccess, user } = useAuth();
  const [organizationId] = useOrganizationId();
  const {
    data: permissionsResponse = { permissions: [] },
    error,
    isLoading,
  } = useFetchUserPermissions({
    userId: user?.id ?? '',
  });
  const { data: rbacFlag } = useFetchFeatureFlag({
    name: 'role-based-access-control-v1',
    organizationId,
  });
  const isPlatformAdminOrStaff = Boolean(user?.isPlatformAdminOrStaff);

  if (availableOnlyForPlatformAdmins) {
    return isPlatformAdminOrStaff;
  }

  if (!rbacFlag?.isEnabled) {
    const requireWritePermissions = requiredPermissions.some(
      (permission) =>
        permission.startsWith('create:') ||
        permission.startsWith('update:') ||
        permission.startsWith('delete:'),
    );
    const isPeoplePage = requiredPermissions.some((permission) =>
      ['update:user', 'delete:user', 'create:user'].includes(permission),
    );
    const isSuborgPage = requiredPermissions.some((permission) =>
      [
        'read:sub-organization',
        'update:sub-organization',
        'delete:sub-organization',
        'create:sub-organization',
      ].includes(permission),
    );

    const isRolesPage = requiredPermissions.some((permission) =>
      ['read:role', 'create:role', 'update:role', 'delete:role'].includes(permission),
    );
    if (isPeoplePage || isSuborgPage) {
      return Boolean(user?.organization?.isOrganizationAdmin) || isPlatformAdminOrStaff;
    }

    if (requireWritePermissions) {
      return checkUserAccess('write');
    }

    if (isRolesPage) {
      return false; // we do not have roles in old flow
    }

    return true; // allow to view all other pages (read permissions) in old flow
  }

  const hasPermissions = requiredPermissions.every((permission) =>
    permissionsResponse.permissions.includes(permission),
  );

  return hasPermissions || isPlatformAdminOrStaff;
};
